import SEO_ROUTES from "@/data/seoRoutes.json";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Leaf, Gem, HeartHandshake, BookOpenText, ScanText, MessageCircleQuestion, Stethoscope, MapPin } from "lucide-react";
import { Seo, organizationSchema, localBusinessSchema, websiteSchema, faqSchema } from "@/components/site/Seo";
import { Reveal } from "@/components/site/Reveal";
import { ProductCard } from "@/components/site/ProductCard";
import { ProductPlaceholder } from "@/components/site/ProductPlaceholder";
import { EnquiryDialog } from "@/components/site/EnquiryDialog";
import { WhatsAppButton } from "@/components/site/WhatsAppButton";
import { SignupForm } from "@/components/site/SignupForm";
import { FaqAccordion } from "@/components/site/FaqAccordion";
import { LifestyleImage } from "@/components/site/PageHero";
import { PRODUCTS, getProduct } from "@/data/products";
import { HOME_FAQS } from "@/data/faqs";
import { IMAGES, BUSINESS, COMMUNITY_OFFER_ACTIVE } from "@/config/site";
import { trackEvent } from "@/lib/analytics";

const VALUES = [
  {
    icon: Leaf,
    title: "Natural Wellness Focus",
    body: "Our range is built around nature-inspired wellness products — from sea moss to hibiscus and beetroot. We describe each product plainly and only publish label details once they are verified.",
  },
  {
    icon: Gem,
    title: "Commitment to Quality",
    body: "Quality is one of our founding values. We take care in how we source, package, supply and distribute every product, and we welcome your questions about any item in our range.",
  },
  {
    icon: HeartHandshake,
    title: "Customer Care and Education",
    body: "We want you to make informed choices. Ask us about sizes, ingredients and ordering, and explore our Wellness Guide as we publish carefully reviewed information.",
  },
];

const INFO_CARDS = [
  { icon: ScanText, title: "Read the label", body: "Ingredients, allergens, storage and expiry information on the product label are your most reliable guide.", to: "/faqs", cta: "Where to find ingredient information" },
  { icon: MessageCircleQuestion, title: "Ask before you buy", body: "Not sure about a product? Ask us about sizes, label details and how ordering works — we’re happy to help.", to: "/contact", cta: "Send us a question" },
  { icon: Stethoscope, title: "Talk to a professional", body: "Wellness products are not a substitute for medical care. Speak to a qualified healthcare professional if you have health concerns.", to: "/health-disclaimer", cta: "Read our health disclaimer" },
];

export default function Home() {
  const seaMoss = getProduct("sea-moss-gel");
  const [enquireOpen, setEnquireOpen] = useState(false);

  return (
    <>
      <Seo
        title={SEO_ROUTES["/"].title}
        description={SEO_ROUTES["/"].description}
        path="/"
        jsonLd={[organizationSchema(), localBusinessSchema(), websiteSchema(), faqSchema(HOME_FAQS)]}
      />

      {/* HERO */}
      <section className="hero-wash noise relative overflow-hidden" data-testid="home-hero">
        <div className="container-site relative grid gap-10 py-14 sm:py-20 lg:grid-cols-12 lg:items-center lg:gap-14 lg:py-24">
          <div className="lg:col-span-6">
            <p className="eyebrow">
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
              Gugulethu, Cape Town · Est. {BUSINESS.established}
            </p>
            <h1 className="h-display mt-4">
              Discover Natural Wellness with <span className="text-[color:var(--u-green)]">Uhlumo Organics</span>
            </h1>
            <p className="lead mt-6 max-w-xl">
              Explore our range of natural wellness products, including Sea Moss Gel, Liquid Chlorophyll, Shilajit-Infused Honey and more.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/products" className="btn btn-primary px-6" data-testid="home-hero-explore-products-button">
                Explore Our Products
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link to="/contact" className="btn btn-outline px-6" data-testid="home-hero-contact-button">
                Contact Us
              </Link>
            </div>
            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
              {["Five wellness products", "Price on request", "Enquire on WhatsApp"].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-[color:var(--u-green)]" aria-hidden="true" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative lg:col-span-6">
            <LifestyleImage id={IMAGES.ocean.id} alt={IMAGES.ocean.alt} priority className="aspect-[4/3] shadow-[0_24px_60px_rgba(37,51,43,0.14)] lg:aspect-[5/4]" sizes="(min-width: 1024px) 45vw, 100vw" caption={false} />
            <Link
              to="/sea-moss-gel"
              className="card-soft card-hover absolute -bottom-6 left-4 right-4 flex items-center gap-4 p-4 sm:left-auto sm:right-6 sm:w-80"
              data-testid="home-hero-featured-sea-moss-link"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#e3eef0] text-[color:var(--u-blue-dark)]">
                <Leaf className="h-5 w-5" aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="badge-featured">Featured</span>
                <span className="mt-1 block font-display text-lg font-semibold">Sea Moss Gel</span>
                <span className="block text-xs text-muted-foreground">Sizes, ingredients &amp; pricing on enquiry</span>
              </span>
              <ArrowRight className="ml-auto h-4 w-4 shrink-0" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCT */}
      <section className="section pt-20 sm:pt-24" aria-labelledby="featured-heading" data-testid="home-featured-sea-moss">
        <div className="container-site">
          <Reveal className="card-soft grid overflow-hidden lg:grid-cols-2">
            <div className="p-3 sm:p-4">
              <ProductPlaceholder product={seaMoss} size="lg" className="aspect-[4/3] h-full min-h-[280px]" />
            </div>
            <div className="flex flex-col justify-center p-7 sm:p-10">
              <p className="eyebrow">Featured product</p>
              <h2 id="featured-heading" className="h-section mt-3">
                Discover Our Sea Moss Gel
              </h2>
              <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground sm:text-base">
                Explore Uhlumo Organics Sea Moss Gel and enquire about available sizes, ingredients, prices and ordering. Sea Moss Gel is a gel prepared from sea moss, a type of red seaweed.
              </p>
              <h3 className="mt-6 text-sm font-semibold uppercase tracking-[0.12em] text-[color:var(--u-brown-dark)]" style={{ fontFamily: "var(--font-body)" }}>
                Ask us about
              </h3>
              <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                {["Available sizes", "Ingredients & label details", "Current pricing", "Ordering & delivery"].map((t) => (
                  <li key={t} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-[color:var(--u-green)]" aria-hidden="true" />
                    {t}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/sea-moss-gel" className="btn btn-primary" data-testid="home-featured-explore-sea-moss-button">
                  Explore Sea Moss Gel
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setEnquireOpen(true);
                    trackEvent("product_enquiry_click", { product: "sea-moss-gel", location: "home-featured" });
                  }}
                  data-testid="home-featured-enquire-sea-moss-button"
                >
                  Enquire About Sea Moss Gel
                </button>
              </div>
            </div>
          </Reveal>
          <EnquiryDialog open={enquireOpen} onOpenChange={setEnquireOpen} product={seaMoss} />
        </div>
      </section>

      {/* COLLECTION */}
      <section className="section surface-sage" aria-labelledby="collection-heading" data-testid="home-product-collection">
        <div className="container-site">
          <Reveal className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="max-w-2xl">
              <p className="eyebrow">The range</p>
              <h2 id="collection-heading" className="h-section mt-3">
                Our Product Collection
              </h2>
              <p className="lead mt-3">Five natural wellness products, each with its own information page. Prices are available on request.</p>
            </div>
            <Link to="/products" className="link-accent inline-flex items-center gap-1.5 text-sm" data-testid="home-collection-view-all-link">
              View all products <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7">
            {PRODUCTS.map((p, i) => (
              <Reveal key={p.slug} delay={i * 60}>
                <ProductCard product={p} />
              </Reveal>
            ))}
            <Reveal delay={320}>
              <div className="flex h-full flex-col justify-between rounded-2xl p-7 surface-green">
                <div>
                  <h3 className="text-2xl font-semibold text-[#FAF9F5]">Not sure where to start?</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#dfe9dc]">Tell us what you are looking for and we will share product information, sizes and prices.</p>
                </div>
                <div className="mt-6 flex flex-col gap-2">
                  <WhatsAppButton className="btn btn-light" testid="home-collection-whatsapp" location="collection" label="Ask on WhatsApp" />
                  <Link to="/contact" className="btn border border-white/30 text-[#FAF9F5] hover:bg-white/10" data-testid="home-collection-contact">
                    Request Product Pricing
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="section" aria-labelledby="why-heading" data-testid="home-why-uhlumo">
        <div className="container-site">
          <Reveal className="mx-auto max-w-2xl text-center">
            <p className="eyebrow justify-center">Why choose us</p>
            <h2 id="why-heading" className="h-section mt-3">
              Why Uhlumo Organics?
            </h2>
            <p className="lead mt-3">Built on three values: quality, integrity and wellness.</p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {VALUES.map(({ icon: Icon, title, body }, i) => (
              <Reveal key={title} delay={i * 80} className="card-soft p-7">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#EEF3EC] text-[color:var(--u-green)]">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="mt-5 text-xl font-semibold">{title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="section surface-sand" aria-labelledby="about-heading" data-testid="home-about">
        <div className="container-site grid gap-10 lg:grid-cols-12 lg:items-center lg:gap-14">
          <Reveal className="lg:col-span-6">
            <LifestyleImage id={IMAGES.coastline.id} alt={IMAGES.coastline.alt} className="aspect-[4/3]" />
          </Reveal>
          <Reveal className="lg:col-span-6">
            <p className="eyebrow">Our story</p>
            <h2 id="about-heading" className="h-section mt-3">
              Wellness Inspired by Nature
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-muted-foreground sm:text-base">
              Uhlumo Organics was founded in {BUSINESS.established} by {BUSINESS.founderShort} with a passion for healthier lifestyles and nature-inspired products. From Gugulethu, Cape Town, we source, package, supply and distribute natural wellness products.
            </p>
            <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground sm:text-base">
              Our mission is to make natural wellness products more accessible, while giving customers useful, honest information about our range.
            </p>
            <Link to="/about" className="btn btn-primary mt-8" data-testid="home-about-discover-story-button">
              Discover Our Story
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* WELLNESS INFORMATION */}
      <section className="section" aria-labelledby="info-heading" data-testid="home-wellness-info">
        <div className="container-site">
          <Reveal className="grid gap-6 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-8">
              <p className="eyebrow">
                <BookOpenText className="h-3.5 w-3.5" aria-hidden="true" />
                Wellness information
              </p>
              <h2 id="info-heading" className="h-section mt-3">
                Make informed choices
              </h2>
              <p className="lead mt-3 max-w-2xl">Helpful, general guidance for choosing wellness products. Our Wellness Guide articles are reviewed before they are published.</p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link to="/wellness-guide" className="btn btn-outline" data-testid="home-wellness-guide-button">
                Visit the Wellness Guide
              </Link>
            </div>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {INFO_CARDS.map(({ icon: Icon, title, body, to, cta }, i) => (
              <Reveal key={title} delay={i * 80} className="card-soft card-hover flex flex-col p-7">
                <Icon className="h-6 w-6 text-[color:var(--u-blue)]" aria-hidden="true" />
                <h3 className="mt-4 text-xl font-semibold">{title}</h3>
                <p className="mt-2 flex-1 text-[15px] leading-relaxed text-muted-foreground">{body}</p>
                <Link to={to} className="link-accent mt-5 text-sm">
                  {cta} →
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY SIGNUP */}
      <section className="surface-green relative overflow-hidden" aria-labelledby="community-heading" id="community" data-testid="home-community-signup">
        <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/5" />
        <div className="container-site relative grid gap-10 py-16 sm:py-20 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-5">
            <p className="eyebrow text-[#cfe2c9]" style={{ color: "#cfe2c9" }}>
              Community
            </p>
            <h2 id="community-heading" className="h-section mt-3 text-[#FAF9F5]">
              Join the Uhlumo Organics Community
            </h2>
            <p className="mt-4 text-base leading-relaxed text-[#dfe9dc]">
              {COMMUNITY_OFFER_ACTIVE
                ? "Receive product news, useful updates and 10% off your first purchase (terms apply)."
                : "Receive product news, useful updates and information from Uhlumo Organics."}
            </p>
            <p className="mt-4 text-sm text-[#cfe2c9]">We will email you to confirm your subscription. Unsubscribe at any time.</p>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-2xl bg-white/[0.06] p-6 ring-1 ring-white/15 sm:p-8">
              <SignupForm idPrefix="home-signup" dark />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section" aria-labelledby="faq-heading" data-testid="home-faqs">
        <div className="container-site grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <p className="eyebrow">FAQs</p>
            <h2 id="faq-heading" className="h-section mt-3">
              Frequently asked questions
            </h2>
            <p className="lead mt-3">Quick answers about our products, ordering and delivery.</p>
            <Link to="/faqs" className="btn btn-outline mt-6" data-testid="home-faqs-view-all">
              View all FAQs
            </Link>
          </Reveal>
          <Reveal className="lg:col-span-8">
            <FaqAccordion faqs={HOME_FAQS} idPrefix="home-faq" />
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="pb-20 sm:pb-24" aria-labelledby="final-cta-heading">
        <div className="container-site">
          <Reveal className="relative overflow-hidden rounded-3xl surface-sage px-6 py-14 text-center sm:px-12 sm:py-16">
            <h2 id="final-cta-heading" className="h-section mx-auto max-w-2xl">
              Explore the Uhlumo Organics Range
            </h2>
            <p className="lead mx-auto mt-4 max-w-xl">Browse our five products, then enquire for sizes, ingredients and pricing.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/products" className="btn btn-primary px-7" data-testid="home-final-cta-view-products-button">
                View Products
              </Link>
              <WhatsAppButton label="WhatsApp Us" testid="home-final-cta-whatsapp-button" location="final-cta" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
